/**
 * Public Local-interface hospital list — `GET /api/v1/hospitals` (M3-T02/T03).
 *
 * Anonymous (no token): the bed-finder is public. The chip selection from
 * `ChipRow` (M3-T06) maps straight to the `row1`/`row2` query params the
 * backend filter (M3-T03) understands. Geo params (lat/lng) land in M3-T09.
 */
import type { ChipSelection } from '../components/chips'
import { ApiError, apiFetch } from '../lib/api'

const NO_FILTER: ChipSelection = { row1: [], row2: null }

/** A visitor location (M3-T09). When supplied, the list sorts nearest-first
 * and each item carries `distance_km`. */
export type GeoPoint = { lat: number; lng: number }

/** Row 1 capability + Row 2 (24hr/night/fast) flags — mirrors the backend
 * `ServiceFlags` schema. Drives the card's service tags. */
export type ServiceFlags = {
  has_emergency: boolean
  has_pharmacy: boolean
  has_lab: boolean
  has_imaging: boolean
  has_surgery: boolean
  has_ambulance: boolean
  has_chronic_care: boolean
  has_specialists_onsite: boolean
  pharmacy_24h: boolean
  lab_24h: boolean
  imaging_24h: boolean
  emergency_24h: boolean
  pharmacy_night: boolean
  lab_night: boolean
  imaging_night: boolean
  emergency_night: boolean
  has_fast_track: boolean
}

/** Bed inventory as returned to the client — mirrors the backend `BedState`. */
export type BedState = {
  icu_total: number
  icu_free: number
  general_total: number
  general_free: number
  private_total: number
  private_free: number
  executive_total: number
  executive_free: number
  maternity_total: number
  maternity_free: number
  emergency_status: 'open' | 'divert' | 'closed'
  ambulance_available: boolean
  updated_at: string
}

export type HospitalListItem = {
  id: string
  slug: string
  name: string
  city: string
  region_code: string
  address: string
  lat: number
  lng: number
  phone: string
  /** Vetted-partner trust badge (read-only display flag). */
  verified: boolean
  /** Indicative "beds from" nightly rate in integer pesewas; null = no rate. */
  bed_rate_from_pesewas: number | null
  /** About-tab content (M12-T03). `description_local`/`whatsapp` may be absent
   * (excluded when null); `accreditations`/`languages` carry seed defaults. */
  description_local?: string | null
  accreditations?: string[]
  languages?: string[]
  whatsapp?: string | null
  beds: BedState
  services: ServiceFlags
  /** Present only when the request supplies lat/lng (M3-T09). */
  distance_km?: number
}

/** One specialty the search understood the query to mean (M22-T05). */
export type ResolvedSpecialty = {
  slug: string
  name: string
  relation: 'definitive' | 'probable' | 'possible'
  /** False = a real discipline no partner staffs. Named, then handed to the
   *  AMTC concierge — never hidden (ADR-0047). */
  offered: boolean
}

/** A procedure the query named (M32-T04). `offer_count` is how many partner
 *  hospitals offer it; 0 means we understood the name and AMTC arranges it. */
export type MatchedProcedure = {
  slug: string
  name: string
  offer_count: number
}

/** What the search read the query as. Present ONLY when the literal match
 *  found nothing and the semantic resolver was used, so its presence is the
 *  signal that we inferred and the patient should be told. */
export type SearchInterpretation = {
  /** `procedure` means the query named a procedure in the catalogue, and the
   *  results are who offers or performs it. */
  route: 'search' | 'emergency' | 'no_match' | 'procedure'
  intent: string
  understood_as: ResolvedSpecialty[]
  /** True when a symptom kept the answer wide. Present these as areas to
   *  consider, NEVER as a finding — symptom is not diagnosis. */
  broadened: boolean
  /** Where this search ends (ADR-0062), decided by the server so the list and
   *  the panel cannot disagree. Absent from a server older than M33-T04. */
  destination?: 'specialists' | 'generalist' | 'concierge' | 'emergency'
  /** Set only when the query named an emergency presentation. */
  red_flag: string | null
  /** Who follows up AFTER the emergency (M33-T02), named beside it and never
   *  instead of it. Absent from a server older than M33-T02, and null when the
   *  phrase carries none. */
  referral?: { slug: string; name: string } | null
  /** The procedures the query named. Absent from a server older than M32-T04,
   *  and empty unless `route` is `procedure`. */
  procedures?: MatchedProcedure[]
}

export type HospitalListResponse = {
  data: HospitalListItem[]
  interpretation?: SearchInterpretation | null
}

export type HospitalSearchResult = {
  data: HospitalListItem[]
  interpretation: SearchInterpretation | null
}

/** Builds the querystring from a chip selection and optional location. Row 1 is
 * repeated (`row1=icu&row1=drugs`); Row 2 is a single optional value; a
 * `GeoPoint` adds `lat`/`lng` (M3-T09). */
export function hospitalsQuery(
  selection: ChipSelection,
  geo?: GeoPoint | null,
  q?: string | null,
): string {
  const params = new URLSearchParams()
  for (const id of selection.row1) params.append('row1', id)
  if (selection.row2) params.append('row2', selection.row2)
  if (geo) {
    params.append('lat', String(geo.lat))
    params.append('lng', String(geo.lng))
  }
  // M22-T09. The text search used to be client-side only, matching hospital
  // name/city/address in the browser — so "skin doctor" matched nothing, and
  // the semantic search added in M22-T06 was never called.
  if (q && q.trim()) params.append('q', q.trim())
  const qs = params.toString()
  return qs ? `?${qs}` : ''
}

/** Stable React Query key for a chip selection + optional location. The geo
 * coords are part of the key so granting location refetches. */
export function hospitalsQueryKey(
  selection: ChipSelection,
  geo?: GeoPoint | null,
  q?: string | null,
) {
  return [
    'hospitals',
    selection.row1,
    selection.row2,
    geo ? [geo.lat, geo.lng] : null,
    q?.trim() || null,
  ] as const
}

/** Fetch the filtered hospital list. Throws `ApiError` on failure. */
export async function fetchHospitals(
  selection: ChipSelection,
  geo?: GeoPoint | null,
  signal?: AbortSignal,
): Promise<HospitalListItem[]> {
  const opts = signal ? { signal } : {}
  const body = await apiFetch<HospitalListResponse>(
    `/api/v1/hospitals${hospitalsQuery(selection, geo)}`,
    opts,
  )
  return body.data
}

/**
 * The same list, plus a text query and what the server made of it (M22-T09).
 *
 * SEPARATE from `fetchHospitals` on purpose. Only the results screen needs the
 * interpretation, and widening the shared function's return type would have
 * churned the bed-type picker and the home strip for no gain.
 */
export async function searchHospitals(
  selection: ChipSelection,
  geo?: GeoPoint | null,
  signal?: AbortSignal,
  q?: string | null,
): Promise<HospitalSearchResult> {
  const opts = signal ? { signal } : {}
  const body = await apiFetch<HospitalListResponse>(
    `/api/v1/hospitals${hospitalsQuery(selection, geo, q)}`,
    opts,
  )
  return { data: body.data, interpretation: body.interpretation ?? null }
}

/** Stable React Query key for a single hospital profile. */
export function hospitalQueryKey(slug: string) {
  return ['hospital', slug] as const
}

/** Fetch one hospital by slug. The list endpoint already returns the full
 * per-hospital payload (M3-T02) and the list is closed at 10, so we read it
 * and select by slug rather than adding a detail route. Throws
 * `ApiError('not_found')` when the slug is unknown. */
export async function fetchHospital(
  slug: string,
  signal?: AbortSignal,
): Promise<HospitalListItem> {
  const all = await fetchHospitals(NO_FILTER, null, signal)
  const match = all.find((h) => h.slug === slug)
  if (!match) {
    throw new ApiError(404, 'not_found', 'Hospital not found')
  }
  return match
}
